import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputAdornment,
  Radio,
  RadioGroup,
  TextField
} from '@mui/material'
import dayjs from 'dayjs'
import React, { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react'

// others
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { AppContext, AuthContext } from '../../context'
import { useCategories } from '../../hooks/useCategories'
import { ManualTimeEntryForm } from '../../interfaceTypes/forms'

// components
import { EntryRateType, Maybe, useDeleteEntryMutation, User, UserType } from '../../../generated'
import { useTimer } from '../../context/TimerContext'
import { useCases } from '../../hooks/useCases'
import { Alert } from '../common/Alert'
import { CustomController } from '../common/CustomController'
import BillableAndNoChargeCheckboxes from './BillableAndNoChargeCheckboxes'
import { AddDuration, SelectCase, SelectUser } from './MatterAndUserFields'
import FeeTypeDropdown from './FeeTypeDropdown'
import { CustomDatePickerController } from '../common/CustomDatePickerRController'

const CommonEntryFields = ({
  handleCancelDialog,
  buttonText,
  showDiscard,
  onlyCloseDialog,
  initialCase,
  clockMode = false,
  forceTimeEntry = false,
  lockCase = false
}: {
  handleCancelDialog: () => void
  buttonText: string
  showDiscard?: boolean
  onlyCloseDialog?: () => void
  initialCase?: React.ComponentProps<typeof SelectCase>['initialCase']
  /** A timer is being stopped, so the clock owns the duration. Every other
   *  field stays editable and is persisted on save. */
  clockMode?: boolean
  /** Render as a TIME entry regardless of the page-level entry type. Set by the
   *  Clock, which lives in the header and is therefore page-agnostic. */
  forceTimeEntry?: boolean
  /** The matter is fixed by the page the dialog was opened from and cannot be changed. */
  lockCase?: boolean
}) => {
  const appContext = useContext(AppContext)
  // The Clock always logs a TIME entry, whichever page it was opened from, so
  // the page-level entry-type flags must not shape this dialog. Without this the
  // flags left behind by /entries/expense (they are never reset on unmount) made
  // the Clock render expense fields and save an expense entry.
  const { isExpenseEntry, isNonClientEntry, isCostEntry, isLegalFee } = forceTimeEntry
    ? { isExpenseEntry: false, isNonClientEntry: false, isCostEntry: false, isLegalFee: false }
    : appContext
  const { currentUser, isAdmin, isParalegal, isInvestigator } = useContext(AuthContext)
  const { closeOrDiscard } = useTimer()
  const {
    control,
    getValues,
    setValue,
    watch,
    formState: { isSubmitting, isValid }
  } = useFormContext<ManualTimeEntryForm>()

  const caseId = useWatch({ name: 'caseId', control })
  const userId = useWatch({ name: 'userId', control }) ?? undefined
  const caseNumber = useWatch({ name: 'caseNumber', control })

  const { categories } = useCategories({ fetchCategory: isExpenseEntry })
  // For admin on matter detail (pre-selected case), don't filter by userId since admin may not be on the legal team.
  // For expense/legal fee with preselected case, skip so MatterDropdownWithForm is the only fetcher.
  const isPreselectedCase = !!caseId && !!caseNumber
  const { cases } = useCases({
    fetchCasesOfUser: isAdmin && isPreselectedCase ? undefined : getValues('userId'),
    searchValue: caseNumber || undefined,
    skipFetch: (isExpenseEntry || isLegalFee) && !!caseId
  })
  // Profile rate of the user picked in <SelectUser>. The dropdown has already
  // resolved that user, so it hands the object up rather than us re-fetching it
  // with our own findOneUser query. When the dropdown isn't rendered (a
  // non-admin logging their own time) the entry is always for the current user,
  // so their own profile rate is the right value.
  const [selectedUser, setSelectedUser] = useState<Maybe<User> | null>(null)

  const userProfileRate = useMemo(() => {
    if (selectedUser?.id === userId) return selectedUser?.hourlyRate ?? 0
    if (userId && userId === currentUser?.id) return currentUser?.hourlyRate ?? 0
    return 0
  }, [selectedUser, userId, currentUser])

  const label = isCostEntry ? 'Select Investigator' : 'Select User'

  // The Rate radio is form state, not component state — it has to be submitted
  // with the entry so it can be restored when the entry is edited later.
  const rateValue = useWatch({ name: 'rateType', control }) ?? EntryRateType.Matter

  // Entry types that render <SelectRate> must always send a rateType, even when
  // the user never touches the radio (Matter is the default selection).
  const showsRateRadio = !isExpenseEntry && !isLegalFee && !isNonClientEntry
  useEffect(() => {
    if (showsRateRadio && !getValues('rateType')) setValue('rateType', EntryRateType.Matter)
  }, [showsRateRadio, getValues, setValue])

  // Rate for the selected user on the selected matter, read from the loaded
  // `cases`. `cases` MUST be a dependency here — otherwise the callback closes
  // over the initial empty list and always returns 0 when the Rate radio is
  // toggled to "Matter".
  //
  // A user can hold SEVERAL CaseToUsers rows on one matter (originator,
  // responsible-attorney, paralegal, …), and some carry a 0/placeholder rate.
  // We must NOT exclude a specific role such as originatorAttorney — a user
  // whose only role is originator still bills at that row's rate. Take the
  // highest assigned rate across all of the user's non-creator rows.
  const computeMatterRate = useCallback(() => {
    if (!caseId) return 0
    const currentUserId = getValues('userId')
    const matter = cases.find(matter => matter?.id === caseId)
    const rates = (matter?.caseToUsers ?? [])
      .filter(member => member?.userId === currentUserId && member?.userType !== UserType.Creator)
      .map(member => member?.hourlyRate ?? 0)
    return rates.length ? Math.max(...rates) : 0
  }, [caseId, cases, getValues])

  const handleRateChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newRate = event.target.value as EntryRateType
      setValue('rateType', newRate)
      setValue('rate', newRate === EntryRateType.Profile ? userProfileRate : computeMatterRate())
    },
    [computeMatterRate, setValue, userProfileRate]
  )

  // Set rate when modal opens with pre-selected matter (e.g., from matter detail page)
  useEffect(() => {
    // Only for entry types that show SelectRate (time entries, cost entries)
    if (isExpenseEntry || isLegalFee || isNonClientEntry) return

    // Only when matter rate is selected
    if (rateValue !== EntryRateType.Matter) return

    // Only override the rate when the selected matter is actually present in
    // the loaded `cases`. Otherwise we'd clobber an already-correct rate (e.g.
    // a running timer's saved rate) with 0 simply because the paginated matter
    // list hasn't loaded this case.
    if (caseId && userId && cases.some(matter => matter?.id === caseId)) {
      setValue('rate', computeMatterRate())
    }
  }, [caseId, userId, cases, rateValue, isExpenseEntry, isLegalFee, isNonClientEntry, computeMatterRate, setValue])

  const computeAmountFromQtyUnitPrice = useCallback(
    (newQty: number | null, newUnitPrice: number | null) => {
      if (newQty != null && newUnitPrice != null) {
        setValue('amount', Math.round(newQty * newUnitPrice * 100) / 100)
      }
    },
    [setValue]
  )

  const AddNotes = useMemo(
    () => (
      <CustomController
        controllerName="description"
        controllerLabel="Description"
        rowsLength={3}
        isMultiLine
        required={false}
      />
    ),
    []
  )

  const AddDate = useMemo(
    () => (
      <CustomDatePickerController
        controllerName="date"
        controllerLabel="Date"
        required
        format="YYYY-MM-DDTHH:mm:ssZ"
        defaultValue={dayjs().local().format('YYYY-MM-DDTHH:mm:ssZ')}
      />
    ),
    []
  )

  const AddAmount = useCallback(
    (label: string) => (
      <CustomController controllerName="amount" controllerLabel={label} fieldType="number" dollarAdornment required />
    ),
    []
  )

  const SelectBillableAndNoCharge = useMemo(
    () => <BillableAndNoChargeCheckboxes control={control} caseId={caseId} setValue={setValue} />,
    [caseId, control, setValue]
  )

  const SelectRate = useMemo(
    () => (
      <Box display={'flex'} alignItems={'self-start'} justifyContent={'space-between'} mb={2}>
        <FormControl>
          <FormLabel id="rate-label" sx={{ fontWeight: 'bold' }}>
            Rate
          </FormLabel>
          <RadioGroup row aria-labelledby="rate-label" name="rate-group" value={rateValue} onChange={handleRateChange}>
            <FormControlLabel value={EntryRateType.Profile} control={<Radio />} label="Profile" />
            <FormControlLabel value={EntryRateType.Matter} control={<Radio />} label="Matter" />
          </RadioGroup>
        </FormControl>

        <Box>
          <CustomController
            required
            controllerName="rate"
            controllerLabel="Rate ($/hr)"
            fieldType="number"
            dollarAdornment
          />
        </Box>
      </Box>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleRateChange, rateValue]
  )

  const SelectFeeType = useMemo(() => <FeeTypeDropdown control={control} name="feeTypeId" required />, [control])

  const SelectCategory = useMemo(
    () => (
      <FormControl variant="outlined" sx={{ marginTop: 1.5, marginBottom: 1 }} fullWidth>
        <Controller
          name="categoryId"
          control={control}
          render={({ field }) => (
            <Autocomplete
              options={categories}
              getOptionLabel={option => option.name}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              onChange={(_, value) => field.onChange(value?.id || null)}
              value={categories.find(c => c.id === field.value) || null}
              renderInput={params => (
                <TextField
                  {...params}
                  placeholder="Select a Category"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: '56px'
                    }
                  }}
                />
              )}
              sx={{
                '& .MuiAutocomplete-input': {
                  height: '14px'
                }
              }}
            />
          )}
        />
      </FormControl>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [categories]
  )

  const renderForm = () => {
    if (isExpenseEntry)
      return (
        <>
          {SelectCategory}
          <Box display="flex" alignItems="flex-start" justifyContent={'center'} gap={2} mb={2}>
            <Controller
              name="qty"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  type="number"
                  margin="dense"
                  fullWidth
                  label="Qty"
                  variant="outlined"
                  value={field.value ?? ''}
                  onChange={e => {
                    const v = e.target.value
                    const newQty = v === '' ? null : Number(v)
                    field.onChange(newQty)
                    const currentUnitPrice = getValues('unitPrice')
                    computeAmountFromQtyUnitPrice(newQty, currentUnitPrice != null ? Number(currentUnitPrice) : null)
                  }}
                  onBlur={field.onBlur}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
            <Controller
              name="unitPrice"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  type="number"
                  margin="dense"
                  fullWidth
                  label="Unit Price"
                  variant="outlined"
                  value={field.value ?? ''}
                  onChange={e => {
                    const v = e.target.value
                    const newUnitPrice = v === '' ? null : Number(v)
                    field.onChange(newUnitPrice)
                    const currentQty = getValues('qty')
                    computeAmountFromQtyUnitPrice(currentQty != null ? Number(currentQty) : null, newUnitPrice)
                  }}
                  onBlur={field.onBlur}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Box>
          <Box display="flex" alignItems="flex-start" justifyContent={'center'} gap={2} mb={2}>
            {AddAmount('Amount')}
            {AddDate}
          </Box>
          {AddNotes}
          {SelectBillableAndNoCharge}
          <Controller
            name="sendNotesToMatterNotes"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={!!field.value} />}
                label="Send Notes to Matter Notes"
                sx={{ mb: 2 }}
              />
            )}
          />
        </>
      )
    else if (isLegalFee)
      return (
        <>
          <Box display="flex" alignItems="flex-start" justifyContent={'center'} gap={2} mb={2}>
            {AddAmount('Fee')}
            {AddDate}
          </Box>
          {SelectFeeType}
          {AddNotes}
          {SelectBillableAndNoCharge}
          <Controller
            name="sendNotesToMatterNotes"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={!!field.value} />}
                label="Send Notes to Matter Notes"
                sx={{ mb: 2 }}
              />
            )}
          />
        </>
      )
    else if (isNonClientEntry)
      return (
        <>
          <Box display="flex" alignItems="flex-start" justifyContent={'center'} gap={2} mb={2}>
            <AddDuration readOnly={clockMode} />
            {AddDate}
          </Box>
          {AddNotes}
        </>
      )
    else {
      return (
        <>
          <Box display="flex" alignItems="flex-start" justifyContent={'center'} gap={2} mb={2}>
            <AddDuration readOnly={clockMode} />
            {AddDate}
          </Box>
          {SelectRate}
          {AddNotes}
          {SelectBillableAndNoCharge}
          <Controller
            name="sendNotesToMatterNotes"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={!!field.value} />}
                label="Send Notes to Matter Notes"
                sx={{ mb: 2 }}
              />
            )}
          />
        </>
      )
    }
  }

  const [deleteTimeEntry, { loading: deleting }] = useDeleteEntryMutation({
    onError: error => {
      Alert.error(`Entry couldn't be discarded due to: ${error.message}`)
    },
    onCompleted: async () => {
      Alert.success('Entry discarded')
      onlyCloseDialog && onlyCloseDialog()
      // Clears timer state, the entry it pointed at and the persisted keys.
      // <Timer> reacts to that by resetting this form to a blank entry — doing
      // a bare `reset()` here instead would restore the discarded entry's
      // values, since they are the form's current defaults.
      closeOrDiscard()
    }
  })

  const deleteEntry = async (entryId: string) => await deleteTimeEntry({ variables: { entryId } })

  const isSaveDisabled = !isExpenseEntry && !isLegalFee && !watch('duration')
  const showStart = !isLegalFee && !isExpenseEntry

  return (
    <Box my={1}>
      {/* Everything stays editable while a timer is being stopped — matter,
          user, rate, notes and the flags are all sent on save. Only Duration is
          held back (see <AddDuration>), because the clock measured it. */}
      <Box sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}>
        {(isAdmin || isParalegal || (!isInvestigator && isCostEntry)) && (
          <SelectUser label={label} rateValue={rateValue} onUserResolved={setSelectedUser} />
        )}
        {!isNonClientEntry && <SelectCase rateValue={rateValue} initialCase={initialCase} lockCase={lockCase} />}
        {renderForm()}
      </Box>

      <DialogActions>
        <Button
          variant="text"
          onClick={() =>
            showDiscard && getValues('entryId') ? deleteEntry(getValues('entryId')) : handleCancelDialog()
          }
          sx={{ color: 'gray' }}
          endIcon={deleting && <CircularProgress size={20} color="inherit" />}
        >
          {showDiscard && getValues('entryId') ? 'Discard' : 'Cancel'}
        </Button>

        {showStart && !clockMode ? (
          <Button type="submit" value="start" variant="contained" color="primary" disabled={!!getValues('duration')}>
            Start
          </Button>
        ) : undefined}

        <Button
          name={'action'}
          value={'save'}
          type="submit"
          variant="contained"
          title={!isValid ? 'Please fill out all required fields' : ''}
          disabled={isSubmitting || isSaveDisabled}
          endIcon={isSubmitting && <CircularProgress size={20} color="inherit" />}
        >
          {buttonText}
        </Button>
      </DialogActions>
    </Box>
  )
}

export default memo(CommonEntryFields)
